/**
 * Ported VERBATIM from `apr_template/src/pages/ScoringModel.tsx`.
 *
 * Only import paths change. The markup, class names, copy, state and logic are the
 * source's — the stylesheet is copied alongside, and the global classes it also uses
 * resolve because the screen renders inside `AprScreen` (`.apr-app`).
 *
 * Do not "tidy" anything here. A difference is a difference from the source.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../compat/AuthContext';
import apiService from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';

const ScoringModelHelp: Help = {
  title: 'Disposition Matrix',
  icon: '🎚️',
  purpose: 'Defines the score bands that turn an application&rsquo;s 1.00&ndash;5.00 score into a <strong>disposition</strong>. Each threshold names a disposition and the min&ndash;max score range that triggers it. Name the dispositions to match whatever model you use.',
  doHere: [
    '<strong>Add Threshold</strong> to map a disposition name to a score range &mdash; use any model you like.',
    'Set each threshold&rsquo;s <strong>min</strong> and <strong>max</strong> score (1.00&ndash;5.00) and a display color.',
    'Use <strong>Edit</strong> / <strong>Delete</strong> on any threshold card to adjust or remove a band.',
    '<strong>Download Data</strong> exports the current thresholds as CSV.'
  ],
  fits: 'Settings &rarr; Disposition Matrix',
  firstTime: 'Confirm the disposition bands cover the full 1.00&ndash;5.00 range with no gaps or overlaps.',
  sections: [
    {
      type: 'defs',
      label: 'Example dispositions',
      items: [
        { term: 'Retire', color: '#7f1d1d', def: '1.00&ndash;2.00 &mdash; decommission the application' },
        { term: 'Re-Platform', color: '#9a3412', def: '2.01&ndash;3.00 &mdash; move to a better platform' },
        { term: 'Re-Design', color: '#9a3412', def: '3.01&ndash;4.00 &mdash; rebuild / modernize' },
        { term: 'Retain', color: '#14532d', def: '4.01&ndash;5.00 &mdash; keep as-is' }
      ]
    }
  ]
};

interface ScoringThreshold {
  id: string;
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
  description: string;
  displayOrder?: number;
}

const ScoringModel: React.FC = () => {
  const { hasPermission } = useAuth();
  const [scoringThresholds, setScoringThresholds] = useState<ScoringThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const canModify = hasPermission('canManageSettings');

  // Load scoring thresholds from database on component mount
  useEffect(() => {
    const loadScoringThresholds = async () => {
      try {
        setLoading(true);
        const thresholds = await apiService.getScoringThresholds();
        setScoringThresholds(thresholds);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load scoring thresholds:', err);
        setError('Failed to load disposition thresholds. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadScoringThresholds();
  }, []);

  const [editingThreshold, setEditingThreshold] = useState<ScoringThreshold | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    minScore: 0,
    maxScore: 0,
    color: '#007bff',
    description: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.includes('Score')) {
      // For score fields, preserve the decimal input
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditThreshold = (threshold: ScoringThreshold) => {
    setEditingThreshold(threshold);
    setFormData({
      name: threshold.name,
      minScore: Number(threshold.minScore.toFixed(2)),
      maxScore: Number(threshold.maxScore.toFixed(2)),
      color: threshold.color,
      description: threshold.description
    });
    setShowEditForm(true);
  };

  const handleCreateThreshold = () => {
    setEditingThreshold(null);
    setFormData({
      name: '',
      minScore: 0,
      maxScore: 0,
      color: '#007bff',
      description: ''
    });
    setShowCreateForm(true);
  };

  const handleDeleteThreshold = async (threshold: ScoringThreshold) => {
    if (!canModify) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete the "${threshold.name}" threshold? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await apiService.deleteScoringThreshold(threshold.id);
      setScoringThresholds(prev => prev.filter(t => t.id !== threshold.id));
    } catch (error: any) {
      console.error('Failed to delete scoring threshold:', error);
      alert('Failed to delete threshold. Please try again.');
    }
  };

  const handleSaveThreshold = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a decision name');
      return;
    }

    // Ensure scores are numbers
    const minScore = typeof formData.minScore === 'string' ? parseFloat(formData.minScore) : formData.minScore;
    const maxScore = typeof formData.maxScore === 'string' ? parseFloat(formData.maxScore) : formData.maxScore;

    if (isNaN(minScore) || minScore < 1 || minScore > 5) {
      alert('Minimum score must be between 1 and 5');
      return;
    }

    if (isNaN(maxScore) || maxScore < 1 || maxScore > 5) {
      alert('Maximum score must be between 1 and 5');
      return;
    }

    if (minScore >= maxScore) {
      alert('Minimum score must be less than maximum score');
      return;
    }

    // Ranges must be unique and non-overlapping: this band's [min, max] must not
    // intersect any OTHER band's range (touching endpoints counts as overlap).
    const conflict = scoringThresholds.find((t) => {
      if (editingThreshold && t.id === editingThreshold.id) return false; // skip self when editing
      return minScore <= t.maxScore && t.minScore <= maxScore;
    });
    if (conflict) {
      alert(
        `This range (${minScore.toFixed(2)}–${maxScore.toFixed(2)}) overlaps "${conflict.name}" ` +
        `(${conflict.minScore.toFixed(2)}–${conflict.maxScore.toFixed(2)}). ` +
        `Disposition ranges must be unique and must not overlap.`
      );
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        minScore: parseFloat(minScore.toFixed(2)),
        maxScore: parseFloat(maxScore.toFixed(2))
      };

      console.log('Saving threshold data:', dataToSave);

      if (editingThreshold) {
        // Update existing threshold
        await apiService.updateScoringThreshold(editingThreshold.id, dataToSave);

        setScoringThresholds(prev => prev.map(threshold =>
          threshold.id === editingThreshold.id
            ? { ...threshold, ...dataToSave }
            : threshold
        ));
      } else {
        // Create new threshold
        const newThreshold = await apiService.createScoringThreshold(dataToSave);
        setScoringThresholds(prev => [...prev, newThreshold]);
      }

      handleCancelEdit();
    } catch (error: any) {
      console.error('Failed to save scoring threshold:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to save threshold: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setShowCreateForm(false);
    setEditingThreshold(null);
    setFormData({ name: '', minScore: 0, maxScore: 0, color: '#007bff', description: '' });
  };

  const downloadScoringData = () => {
    if (scoringThresholds.length === 0) {
      alert('No scoring thresholds data to download');
      return;
    }

    const headers = ['Disposition', 'Min Score', 'Max Score', 'Color', 'Description'];
    const csvRows = [headers.join(',')];

    scoringThresholds.forEach(threshold => {
      const row = [
        `"${(threshold.name || '').replace(/"/g, '""')}"`,
        threshold.minScore.toFixed(2),
        threshold.maxScore.toFixed(2),
        `"${(threshold.color || '').replace(/"/g, '""')}"`,
        `"${(threshold.description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scoring_thresholds_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Disposition Matrix" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <PageHeader title="Disposition Matrix" />
        <div className="error-message">
          <p style={{ color: '#dc3545' }}>⚠️ {error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <HelpPanel help={ScoringModelHelp} />
      <PageHeader
        title="Disposition Matrix"
        actions={<>
          {canModify && (
            <button
              className="btn btn-primary"
              onClick={handleCreateThreshold}
            >
              ➕ Add Threshold
            </button>
          )}
          {scoringThresholds.length > 0 && (
            <button
              className="btn btn-outline-success"
              onClick={downloadScoringData}
              title="Download current scoring thresholds data as CSV"
              style={{ border: '2px solid #28a745' }}
            >
              📥 Download Data
            </button>
          )}
        </>}
      />

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Edit/Create Threshold Form Modal */}
      {(showEditForm || showCreateForm) && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '600px',
            maxWidth: '700px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
                {editingThreshold ? 'Edit Disposition' : 'Create New Disposition'}
              </h2>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveThreshold(); }}>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="name" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Disposition Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. Retire, Modernize, Retain, Invest…"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label htmlFor="minScore" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Minimum Score *
                  </label>
                  <input
                    type="text"
                    id="minScore"
                    name="minScore"
                    value={typeof formData.minScore === 'string' ? formData.minScore : (formData.minScore === 0 ? '' : formData.minScore.toFixed(2))}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and decimal point (including partial decimals)
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        // Store as string during typing to preserve partial input
                        setFormData(prev => ({
                          ...prev,
                          minScore: value as any
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Format to 2 decimals on blur and convert to number
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 1 && numValue <= 5) {
                        setFormData(prev => ({
                          ...prev,
                          minScore: parseFloat(numValue.toFixed(2))
                        }));
                      } else {
                        // Reset to 0 if invalid
                        setFormData(prev => ({
                          ...prev,
                          minScore: 0
                        }));
                      }
                    }}
                    placeholder="1.00"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="maxScore" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Maximum Score *
                  </label>
                  <input
                    type="text"
                    id="maxScore"
                    name="maxScore"
                    value={typeof formData.maxScore === 'string' ? formData.maxScore : (formData.maxScore === 0 ? '' : formData.maxScore.toFixed(2))}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and decimal point (including partial decimals)
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        // Store as string during typing to preserve partial input
                        setFormData(prev => ({
                          ...prev,
                          maxScore: value as any
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Format to 2 decimals on blur and convert to number
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 1 && numValue <= 5) {
                        setFormData(prev => ({
                          ...prev,
                          maxScore: parseFloat(numValue.toFixed(2))
                        }));
                      } else {
                        // Reset to 0 if invalid
                        setFormData(prev => ({
                          ...prev,
                          maxScore: 0
                        }));
                      }
                    }}
                    placeholder="5.00"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="color" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Display Color
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="color"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    style={{
                      width: '50px',
                      height: '40px',
                      padding: '0',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  />
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: formData.color,
                      border: '2px solid #fff',
                      borderRadius: '50%',
                      boxShadow: '0 0 0 1px #ddd',
                      flexShrink: 0
                    }}
                    title={`Preview: ${formData.color}`}
                  ></div>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow # and hex characters (0-9, a-f, A-F)
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '#') {
                        setFormData(prev => ({
                          ...prev,
                          color: value
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure valid hex color format on blur
                      let value = e.target.value;
                      if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
                        // If invalid, revert to previous valid color or default
                        if (value.length < 7) {
                          value = '#007bff'; // default color
                        }
                        setFormData(prev => ({
                          ...prev,
                          color: value
                        }));
                      }
                    }}
                    placeholder="#007bff"
                    style={{
                      width: '100px',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="description" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe when this disposition should be applied"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#999';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#333',
                    border: '1px solid #ddd',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#1565c0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#1976d2';
                  }}
                  style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                >
                  {editingThreshold ? 'Save Threshold' : 'Create Threshold'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disposition Thresholds Section */}
      <div className="thresholds-container">
        <h3>Disposition Thresholds</h3>
        <div className="thresholds-list">
          {scoringThresholds.length === 0 ? (
            <div className="empty-state">
              <p>No disposition thresholds found. Please contact your administrator.</p>
            </div>
          ) : (
            scoringThresholds.map(threshold => (
            <div key={threshold.id} className="threshold-card">
              <div className="threshold-header">
                <div className="threshold-info">
                  <div 
                    className="threshold-color"
                    style={{ backgroundColor: threshold.color }}
                  ></div>
                  <div>
                    <h4>{threshold.name}</h4>
                    <span className="score-range">
                      {threshold.minScore.toFixed(2)} - {threshold.maxScore.toFixed(2)}
                    </span>
                  </div>
                </div>
                {canModify && (
                  <div className="threshold-actions">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEditThreshold(threshold)}
                      title="Edit threshold"
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteThreshold(threshold)}
                      title="Delete threshold"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <p className="threshold-description">{threshold.description}</p>
            </div>
            ))
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ScoringModel;